import {ChangeDetectorRef, Injectable} from '@angular/core';
import {debounceTime, Subject, Subscription, tap} from 'rxjs';
import {
  Cart,
  CartEvent,
  EventPricing,
  EventPricingTicketType,
  TicketsRequest,
  TicketsResponse,
  UnassignedTicket
} from '../../generated/sales-terminal-client';
import {finalize} from 'rxjs/operators';
import {CartAdvancedInterface} from '../cart/cart.interface';
import {CartService} from '../cart/cart.service';
import moment from 'moment/moment';
import {StorageService} from '../storage/storage.service';

@Injectable({
  providedIn: 'root'
})
export class TicketTypeService {
  private _subscriptions: Subscription[] = [];
  public typeTempCounts: Record<string, number> = {};
  private _typeInitialCounts: Record<string, number> = {};
  public typeQuantities: Record<string, number> = {};
  public typeSubjects: Record<string, Subject<TicketsRequest[]>> = {};
  public typeSubjectBuffer: Record<string, Record<string, TicketsRequest>> = {}; // eventId -> ticketTypeId + ticketsRequest
  public typesLoading: boolean;
  public ticketTypeCountMap: Record<string, number> = {};
  public changeDetector: ChangeDetectorRef;

  constructor(private storageService: StorageService,
              private cartService: CartService) {}

  public initTicketTypeHandling(service: CartAdvancedInterface<any>, events: CartEvent[], changeDetector?: ChangeDetectorRef): void {
    this.typeSubjects = {};
    this.typeSubjectBuffer = {};
    if (changeDetector) {
      this.changeDetector = changeDetector;
    }
    this.clearSubscriptions();
    events?.forEach(event => {
      if (event.boEvent) {
        this.typeSubjects[event.id] = new Subject<TicketsRequest[]>();
        this.typeSubjectBuffer[event.id] = {};
        if (event.unassignedTickets?.length) {
          event.unassignedTickets.forEach(eventTicketType => {
            this._typeInitialCounts[eventTicketType.ticketTypeUuid] = eventTicketType.quantity;
            this.typeQuantities[eventTicketType.ticketTypeUuid] = eventTicketType.quantity;
          });
        }
        this._subscriptions.push(
          this.typeSubjects[event.id].pipe(debounceTime(1000)).subscribe((ticketsRequests: TicketsRequest[]) => {
            if (ticketsRequests?.length) {
              this.toggleLoading(true);
              service.setUnassignedTickets(event.id, ticketsRequests).pipe(
                finalize(() => {
                  this.typeTempCounts = {};
                  this.setInitialCounts(service.cart);
                  this.typeSubjectBuffer[event.id] = {};
                  this.toggleLoading(false);
                })
              ).subscribe((response: TicketsResponse) => {
                service.cart = response.cart;
                this.updateCart(service, event.id, response.pricing);
              });
            }
          })
        );
      }
    });
  }
  private toggleLoading(state: boolean): void {
    this.typesLoading = state;
    this.cartService.isLoading = state;
    if (this.changeDetector) {
      this.changeDetector.markForCheck();
    }
  }
  private updateCart(service: CartAdvancedInterface<any>, eventId: number, eventPricing: EventPricing): void {
    service.caller = true;
    this.storageService.set(CartService.STORAGE_CART_UPDATE, moment.now());
    service.updateCartForEvent(eventId);
    service.eventPricing = eventPricing;
  }
  private setInitialCounts(cart: Cart): void {
    this._typeInitialCounts = {};
    this.typeQuantities = {};
    if (cart?.events?.length) {
      cart.events.forEach(event => {
        if (event.unassignedTickets?.length) {
          event.unassignedTickets.forEach(eventTicketType => {
            this._typeInitialCounts[eventTicketType.ticketTypeUuid] = eventTicketType.quantity;
            this.typeQuantities[eventTicketType.ticketTypeUuid] = eventTicketType.quantity;
          });
        }
      });
    }
  }

  public changeTicketTypeCount(ticketTypeUuid: string, eventId: number, count?: number): void {
    if (this.typesLoading || this.cartService.isLoading) return;
    if (!this.typeSubjectBuffer[eventId]) {
      this.typeSubjectBuffer[eventId] = {};
    }
    this.typeQuantities[ticketTypeUuid] = count;
    this.typeTempCounts[ticketTypeUuid] = count - this._typeInitialCounts[ticketTypeUuid];
    if (this.typeTempCounts[ticketTypeUuid] === 0) {
      delete this.typeSubjectBuffer[eventId][ticketTypeUuid];
    } else {
      this.typeSubjectBuffer[eventId][ticketTypeUuid] = {
        ticketTypeUuid: ticketTypeUuid,
        quantity: this.typeTempCounts[ticketTypeUuid]
      };
    }
    this.typeSubjects[eventId]?.next(Object.values(this.typeSubjectBuffer[eventId]));
  }

  public resetTicketTypeCounts(): void {
    this.ticketTypeCountMap = {};
    if (this.cartService.eventPricing?.ticketTypes?.length) {
      this.cartService.eventPricing.ticketTypes.forEach((ticketType: EventPricingTicketType) => {
        this.ticketTypeCountMap[ticketType.uuid] = 0;
      });
    }
  }

  public getTicketTypeAvailable(id: string, eventId: number): number {
    const eventPricing: EventPricing = this.cartService.eventPricingMap ? this.cartService.eventPricingMap[eventId] : null;
    const ticketType: EventPricingTicketType = eventPricing?.ticketTypes.find(type => type.uuid === id);
    return ticketType?.available ?? (eventPricing?.available || 0);
  }
  public getTicketTypeInitialCount(id: string): number {
    return this._typeInitialCounts[id] || 0;
  }

  public clearSubscriptions(): void {
    this._subscriptions.forEach(s => s.unsubscribe());
    this._subscriptions = [];
  }
}
